import React from 'react';
import { SparkIcon, TooltipTrigger } from '@spark';
import FieldCard from '@/shared/components/FieldCard';

/**
 * Right-hand "Available fields" panel of the Map prompt template modal:
 * custom fields (with empty state) and dataset columns (with value preview,
 * occurrence count, and mapped / insert / primary state icon).
 *
 * @param columns [{ name, value, textual }] — file columns except the example field
 * @param customFields { fieldName: originalText }
 * @param customFieldPreviews { fieldName: currentText } — what each placeholder
 *   stands for in the current example (falls back to the stored original text)
 */
const AvailableFieldsPanel = ({
    columns,
    customFields,
    customFieldPreviews,
    occurrences,
    mappedFields,
    primaryField,
    loading = false,
    /** Shuffle: cards keep name and state, only value previews reload. */
    valuesLoading = false,
    interactive,
    onToggleColumn,
    onToggleCustomField,
    onDeleteCustomField,
    /** Hover pin on a mapped, non-primary card: make it the primary input. */
    onSetPrimary,
}) => {
    const customEntries = Object.entries(customFields || {});
    const mapped = new Set(mappedFields || []);
    const canInteract = Boolean(interactive && !loading && !valuesLoading);
    // Set-primary (hover pin) and delete (hover trash) don't edit the template
    // around a caret, so unlike card toggling they work on both tabs — they
    // hide only while the cards are loading or shuffle reloads the values.
    const canQuickAct = Boolean(!loading && !valuesLoading);

    return (
        <aside className="cw-pm-fields-panel">
            <div className="cw-pm-fields-panel__header">
                <h4 className="cw-pm-fields-panel__title">Available fields</h4>
                <p className="cw-pm-fields-panel__hint">
                    Click to insert · type / or {'{{'}
                </p>
            </div>

            <section className="cw-pm-fields-panel__section">
                <h5 className="cw-pm-fields-panel__section-title">
                    Custom fields
                    {customEntries.length ? ` (${customEntries.length})` : ''}
                </h5>
                {customEntries.length === 0 ? (
                    <div className="cw-pm-fields-panel__empty">
                        <p className="cw-pm-fields-panel__empty-title">No custom fields yet</p>
                        <p className="cw-pm-fields-panel__empty-hint">
                            Select text, or type / or {'{{'}.
                        </p>
                    </div>
                ) : (
                    customEntries.map(([name, originalText], index) => (
                        // The delete action sits OUTSIDE the card, in a gutter
                        // whose width is always reserved (hover only fades the
                        // trash in, never resizes the card): the card toggles
                        // map/unmap, the trash deletes the field for good.
                        <div key={name} className="cw-pm-field-card-row">
                            <FieldCard
                                name={name}
                                value={customFieldPreviews?.[name] || originalText}
                                typeIconSlug="text"
                                variant="custom"
                                loading={loading}
                                valueLoading={valuesLoading}
                                skeletonIndex={index}
                                occurrences={occurrences?.[name]}
                                isMapped={mapped.has(name)}
                                isPrimary={name === primaryField}
                                interactive={canInteract}
                                onClick={() => onToggleCustomField(name, mapped.has(name))}
                                onSetPrimary={
                                    canQuickAct && onSetPrimary
                                        ? () => onSetPrimary(name)
                                        : undefined
                                }
                            />
                            {canQuickAct && onDeleteCustomField ? (
                                <TooltipTrigger
                                    text="Delete field"
                                    className="cw-pm-field-card-row__delete"
                                >
                                    <button
                                        type="button"
                                        aria-label={`Delete custom field ${name}`}
                                        className="cw-pm-field-card-row__delete-btn"
                                        onClick={() => onDeleteCustomField(name)}
                                    >
                                        <SparkIcon slug="trash" size="small" />
                                    </button>
                                </TooltipTrigger>
                            ) : null}
                        </div>
                    ))
                )}
            </section>

            <section className="cw-pm-fields-panel__section">
                <h5 className="cw-pm-fields-panel__section-title">
                    Dataset columns ({(columns || []).length})
                </h5>
                {(columns || []).length === 0 ? (
                    <div className="cw-pm-fields-panel__empty">
                        <p className="cw-pm-fields-panel__empty-title">
                            No columns from this dataset.
                        </p>
                    </div>
                ) : (
                    columns.map(({ name, value, textual }, index) => (
                        <FieldCard
                            key={name}
                            name={name}
                            value={value}
                            typeIconSlug={textual ? 'text' : 'code-dots'}
                            loading={loading}
                            valueLoading={valuesLoading}
                            skeletonIndex={index}
                            occurrences={occurrences?.[name]}
                            isMapped={mapped.has(name)}
                            isPrimary={name === primaryField}
                            interactive={canInteract}
                            onClick={() => onToggleColumn(name, mapped.has(name))}
                            onSetPrimary={
                                canQuickAct && onSetPrimary ? () => onSetPrimary(name) : undefined
                            }
                        />
                    ))
                )}
            </section>
        </aside>
    );
};

export default AvailableFieldsPanel;
